import { c as _c } from "react/compiler-runtime";
import { queryClient } from "@/config/react-query";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import type { AlertDetail, FullscriptTreatmentPlanUpdatedData } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import { fullscriptQueryKeys } from "@/query/integrations/fullscript";
import { patientSupplementQueryKeys } from "@/query/patient/supplements";

/**
 * Subscribe to Fullscript treatment-plan websocket events and keep the open
 * Supplements tab in sync — without the full vendor sync.
 *
 * Mirrors useLabRealtimeCache: the Fullscript webhook has already upserted the
 * resolved plan/order row into the local DB, so the push is a light signal
 * (ids only — no product names / PHI). On a message whose patient matches, we
 * invalidate the patient's treatment-plan query, which refetches the cheap
 * local-DB list GET. This is NOT the full `sync_patient` (that runs once, when a
 * chart is linked).
 *
 * This is the one cache update in the tab that refetches instead of patching,
 * because the payload carries no plan data to patch *with* — the push is a
 * signal, and the row it refers to may not even be in the cache yet (a sent
 * recommendation is a brand-new row; an order needs totals and line items).
 * That refetch is only trustworthy because the list endpoint is primary-served:
 * off the read pool it could land inside the replication window and hold the
 * pre-write rows as fresh. See list_fullscript_treatment_plans.
 */
export function useFullscriptRealtimeCache() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = function handleMessage(message) {
      if (message.type !== AlertType.FULLSCRIPT_TREATMENT_PLAN_UPDATED) {
        return;
      }
      const data = message.data as FullscriptTreatmentPlanUpdatedData | undefined;
      if (!data?.patient_id) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: fullscriptQueryKeys.treatmentPlans(data.patient_id)
      });
      queryClient.invalidateQueries({
        queryKey: patientSupplementQueryKeys.patient(data.patient_id)
      });
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const handleMessage = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [AlertType.FULLSCRIPT_TREATMENT_PLAN_UPDATED];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useWebSocketCallbackSubscription(t1, handleMessage, true, "useFullscriptRealtimeCache");
}